import type { ReactNode } from 'react';

export interface TagProps {
  children: ReactNode;
  /** Filled/selected visual state (e.g. an industry already chosen). */
  selected?: boolean;
  /** Makes the tag clickable to select it (e.g. from an "add more" list). */
  onClick?: () => void;
  /** Renders a remove ("x") affordance and makes it clickable. */
  onRemove?: () => void;
  disabled?: boolean;
}

const BASE = 'inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-colors';

/**
 * Chip primitive with three shapes driven by props: static read-only display
 * (view mode), a selectable outlined pill (`onClick`, "add more" lists), and a
 * filled removable pill (`onRemove`, currently-selected values).
 */
export function Tag({ children, selected = false, onClick, onRemove, disabled = false }: TagProps) {
  if (onRemove) {
    return (
      <span className={`${BASE} bg-navy text-white`}>
        {children}
        <button
          type="button"
          onClick={onRemove}
          disabled={disabled}
          aria-label={`Remove ${children}`}
          className="rounded-full hover:opacity-75 disabled:opacity-50"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
            <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      </span>
    );
  }

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-pressed={selected}
        className={`${BASE} border ${
          selected ? 'border-navy bg-navy text-white' : 'border-input-border bg-white text-navy hover:border-navy'
        } disabled:cursor-not-allowed disabled:opacity-50`}
      >
        {children}
      </button>
    );
  }

  return <span className={`${BASE} border border-input-border bg-white text-navy`}>{children}</span>;
}
